/**
 * `hermes`, `followUps` and `drafts` — design §2.2, §2.4 and §2.5.
 *
 * ## The one thing every shape here has in common
 *
 * **Evidence.** A digest item, a follow-up and a draft each carry the refs they
 * were built from, and the UI renders them as links into the timeline. §2.4:
 * "Nothing enters the digest without traceable evidence — this is the
 * anti-hallucination spine of the whole feature." A contract that let the server
 * return a reason without its citations would make that spine optional at the
 * one boundary where it is visible to a person.
 *
 * ## What is deliberately NOT on the wire
 *
 *  - **No `drafts.send` for SMS or LinkedIn.** §2.5 is explicit that neither is
 *    server-sendable: SMS from a Twilio number "reads as spam and breaks the
 *    existing thread", and LinkedIn has no member-to-member API at any partner
 *    tier. `drafts.markCopied` and `drafts.markSent` are the honest surface for
 *    those two, and a `sendSms` procedure would be a promise the platform cannot
 *    keep.
 *  - **No `hermes.digest.generate` without a date.** A regenerate names the day
 *    it re-runs, so an accidental double-tap cannot produce two digests.
 *  - **No memory DELETE.** §2.2: memory is append-only with supersede pointers.
 *    `memories.deactivate` retracts; nothing erases.
 */

import { z } from 'zod';
import { isoDateTimeSchema, jsonObjectSchema, nonBlankString, uuidSchema } from './common.ts';
import { channelSchema } from './enums.ts';

// --- shared vocabulary -------------------------------------------------------

/**
 * §2.4 step 1's signal table. (h) and (i) are the design's phase-8 pair and
 * landed with Wave-3 lane S2: (h) is a job change spotted by an enrichment
 * refresh, (i) is a promised introduction whose subject now exists in the CRM.
 */
export const DIGEST_SIGNALS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i'] as const;
export const digestSignalSchema = z.enum(DIGEST_SIGNALS);

export const DIGEST_ITEM_KINDS = ['follow_up', 'meeting_prep', 'enrichment_action'] as const;
export const digestItemKindSchema = z.enum(DIGEST_ITEM_KINDS);

export const DIGEST_STATUSES = [
  'pending',
  'generating',
  'ready',
  'delivered',
  'failed',
  'skipped',
] as const;
export const digestStatusSchema = z.enum(DIGEST_STATUSES);

export const EVIDENCE_KINDS = [
  'contact_created',
  'interaction',
  'message_thread',
  'commitment',
  'calendar_event',
  'meeting',
  'event',
  /** A `contact_field_candidates` row — signal (h)'s citation into the review queue. */
  'enrichment',
] as const;
export const evidenceKindSchema = z.enum(EVIDENCE_KINDS);

/**
 * One citation. `kind` + `id` is what the web card turns into a link, which is
 * why the union is flat rather than per-kind payloads: the renderer routes on
 * `kind` and displays everything else identically.
 */
export const evidenceRefSchema = z.object({
  kind: evidenceKindSchema,
  id: z.string().min(1),
  at: isoDateTimeSchema.nullable(),
  label: z.string(),
  /** Verbatim, for a commitment. The phrase a draft is allowed to quote. */
  quote: z.string().nullable().optional(),
});

export const FOLLOW_UP_STATES = [
  'suggested',
  'accepted',
  'snoozed',
  'drafted',
  'sent',
  'done',
  'dismissed',
] as const;
export const followUpStateSchema = z.enum(FOLLOW_UP_STATES);

export const DRAFT_STATUSES = [
  'draft',
  'approved',
  'queued',
  'sent',
  'failed',
  'discarded',
] as const;
export const draftStatusSchema = z.enum(DRAFT_STATUSES);

export const FEEDBACK_ACTIONS = [
  'accepted',
  'edited',
  'rejected',
  'snoozed',
  'never_suggest',
  'sent_different_channel',
] as const;
export const feedbackActionSchema = z.enum(FEEDBACK_ACTIONS);

export const MEMORY_KINDS = [
  'preference',
  'fact_about_user',
  'fact_about_contact',
  'relationship_note',
  'commitment_context',
  'feedback_pattern',
] as const;
export const memoryKindSchema = z.enum(MEMORY_KINDS);

// --- drafts ------------------------------------------------------------------

export const draftSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema,
  followUpId: uuidSchema.nullable(),
  digestId: uuidSchema.nullable(),
  channel: channelSchema,
  subject: z.string().nullable(),
  body: z.string(),
  charCount: z.int().min(0).nullable(),
  status: draftStatusSchema,
  /** Where it goes: an address, a number, or a captured LinkedIn thread URL. */
  targetAddress: z.string().nullable(),
  targetThreadUrl: z.string().nullable(),
  /** True once the user has touched the text. The diff is the learning signal. */
  editedByUser: z.boolean(),
  originalBody: z.string().nullable(),
  /** Which supplied evidence refs the generated text leaned on. */
  referencesUsed: z.array(z.string()),
  /** What the model asserted but could not ground. Badged, never dropped. */
  unverifiedClaims: z.array(z.string()),
  confidence: z.number().min(0).max(1).nullable(),
  modelId: z.string().nullable(),
  /** The server half of the 30-second undo (§2.5). Null when not approved. */
  undoUntil: isoDateTimeSchema.nullable(),
  copiedAt: isoDateTimeSchema.nullable(),
  sentAt: isoDateTimeSchema.nullable(),
  sendError: z.string().nullable(),
  regeneratedCount: z.int().min(0),
  createdAt: isoDateTimeSchema,
});

// --- digest ------------------------------------------------------------------

export const digestItemSchema = z.object({
  id: uuidSchema,
  kind: digestItemKindSchema,
  rank: z.int().min(1),
  contactId: uuidSchema,
  contactName: z.string(),
  companyName: z.string().nullable(),
  contactType: z.string().nullable(),
  followUpId: uuidSchema.nullable(),
  signal: digestSignalSchema.nullable(),
  /** The card headline. Composed by the model, or the deterministic fallback. */
  reason: z.string(),
  score: z.number(),
  /** The five weighted terms and two penalties AS SCORED, for the why-this card. */
  scoreBreakdown: jsonObjectSchema,
  evidence: z.array(evidenceRefSchema),
  channel: channelSchema.nullable(),
  channelReason: z.string().nullable(),
  /** §2.4 step 6: a dropped draft flags the item; it never removes it. */
  flagged: z.boolean(),
  flaggedReason: z.string().nullable(),
  outcome: z.string().nullable(),
  draft: draftSchema.nullable(),
});

export const digestSchema = z.object({
  id: uuidSchema,
  localDate: z.string(),
  timezone: z.string(),
  status: digestStatusSchema,
  headline: z.string().nullable(),
  focusNote: z.string().nullable(),
  itemCount: z.int().min(0),
  /** Soft cap or no model: items shipped, drafts did not. */
  degraded: z.boolean(),
  degradedReason: z.string().nullable(),
  /** Why a digest is MISSING. A skipped run must be legible, not silent. */
  skipReason: z.string().nullable(),
  personaVersion: z.int().min(1).nullable(),
  deliveredChannels: z.array(z.string()),
  generatedAt: isoDateTimeSchema.nullable(),
  deliveredAt: isoDateTimeSchema.nullable(),
  openedAt: isoDateTimeSchema.nullable(),
  items: z.array(digestItemSchema),
});

export const digestTodayInputSchema = z.object({
  /**
   * `YYYY-MM-DD` in the user's own timezone. Absent means "the newest one",
   * NOT "today or nothing": at 06:55 on Tuesday the right answer is Monday's
   * digest, and the client labels it from `localDate`.
   */
  localDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

export const digestGetInputSchema = z.object({ digestId: uuidSchema });

export const digestRegenerateInputSchema = z.object({
  /** Named explicitly, so a double-tap cannot produce two digests. */
  localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const digestRegenerateOutputSchema = z.object({
  ok: z.literal(true),
  /** The digest is rebuilt on the WORKER; this acknowledges the enqueue. */
  queued: z.boolean(),
  localDate: z.string(),
  reason: z.string().nullable(),
});

// --- profile (L1) ------------------------------------------------------------

export const hermesToneSchema = z.enum(['warm_direct', 'formal', 'casual', 'concise']);

export const hermesProfileSchema = z.object({
  role: z.string().nullable(),
  company: z.string().nullable(),
  raiseActive: z.boolean(),
  raiseStage: z.string().nullable(),
  raiseTarget: z.string().nullable(),
  sectors: z.array(z.string()),
  geo: z.string().nullable(),
  tone: hermesToneSchema,
  toneNotes: z.string().nullable(),
  signatureBlock: z.string().nullable(),
  /** Measured from sent mail and PROPOSED, never silently applied (§2.2). */
  typicalMsgLenChars: z.int().min(1).nullable(),
  languages: z.array(z.string()),
  smsAllowedFor: z.array(z.string()),
  digestItemCount: z.int().min(3).max(10),
  dailySendCap: z.int().min(1).max(200),
  perContact7dCap: z.int().min(1).max(20),
  quietHoursStart: z.int().min(0).max(23),
  quietHoursEnd: z.int().min(1).max(24),
  cadenceRules: jsonObjectSchema,
  channelPreference: jsonObjectSchema,
  /** The current persona document and its version, for the settings screen. */
  personaVersion: z.int().min(0),
  personaBodyMd: z.string().nullable(),
  personaBuiltFor: z.string().nullable(),
  updatedAt: isoDateTimeSchema,
});

export const hermesProfileUpdateInputSchema = z.object({
  role: z.string().max(200).nullish(),
  company: z.string().max(200).nullish(),
  raiseActive: z.boolean().optional(),
  raiseStage: z.string().max(80).nullish(),
  raiseTarget: z.string().max(80).nullish(),
  sectors: z.array(z.string().max(80)).max(20).optional(),
  geo: z.string().max(120).nullish(),
  tone: hermesToneSchema.optional(),
  toneNotes: z.string().max(2000).nullish(),
  signatureBlock: z.string().max(2000).nullish(),
  typicalMsgLenChars: z.int().min(1).max(4000).nullish(),
  languages: z.array(z.string().max(16)).max(10).optional(),
  smsAllowedFor: z.array(z.string().max(32)).max(10).optional(),
  digestItemCount: z.int().min(3).max(10).optional(),
  dailySendCap: z.int().min(1).max(200).optional(),
  perContact7dCap: z.int().min(1).max(20).optional(),
  quietHoursStart: z.int().min(0).max(23).optional(),
  quietHoursEnd: z.int().min(1).max(24).optional(),
  cadenceRules: jsonObjectSchema.optional(),
  channelPreference: jsonObjectSchema.optional(),
});

// --- memories (L3) -----------------------------------------------------------

/**
 * §2.2: "**Visibility is mandatory.** The app shows 'Hermes learned: *you never
 * use exclamation points*' with an **Undo**. A memory the user cannot see or
 * delete is a trust bomb."
 */
export const hermesMemorySchema = z.object({
  id: uuidSchema,
  kind: memoryKindSchema,
  content: z.string(),
  confidence: z.number().min(0).max(1),
  contactId: uuidSchema.nullable(),
  isActive: z.boolean(),
  supersededById: uuidSchema.nullable(),
  sourceType: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});

export const hermesMemoriesListInputSchema = z.object({
  kind: memoryKindSchema.optional(),
  contactId: uuidSchema.optional(),
  /** `false` includes retracted rows — provenance survives a retraction (§2.2). */
  activeOnly: z.boolean().default(true),
  limit: z.int().min(1).max(200).default(50),
});

export const hermesMemoriesListOutputSchema = z.object({
  items: z.array(hermesMemorySchema),
});

export const hermesMemoryDeactivateInputSchema = z.object({
  memoryId: uuidSchema,
});

// --- feedback ----------------------------------------------------------------

export const hermesFeedbackInputSchema = z.object({
  action: feedbackActionSchema,
  digestItemId: uuidSchema.optional(),
  draftId: uuidSchema.optional(),
  followUpId: uuidSchema.optional(),
  contactId: uuidSchema.optional(),
  channel: channelSchema.optional(),
  /** What the user actually sent, when it differs from what was drafted. */
  editedText: z.string().max(20_000).optional(),
  reason: z.string().max(1000).optional(),
});

export const hermesFeedbackOutputSchema = z.object({
  ok: z.literal(true),
  feedbackId: uuidSchema,
  /** The outcome stamped on the digest item, when one was named. */
  outcome: z.string().nullable(),
});

// --- follow-ups --------------------------------------------------------------

export const followUpSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema,
  contactName: z.string(),
  companyName: z.string().nullable(),
  state: followUpStateSchema,
  source: z.string(),
  signal: digestSignalSchema.nullable(),
  reason: z.string(),
  reasonCodes: z.array(z.string()),
  evidence: z.array(evidenceRefSchema),
  scoreBreakdown: jsonObjectSchema,
  priorityScore: z.number(),
  preferredChannel: channelSchema.nullable(),
  channelReason: z.string().nullable(),
  dueAt: isoDateTimeSchema.nullable(),
  snoozedUntil: isoDateTimeSchema.nullable(),
  snoozeReason: z.string().nullable(),
  dismissedReason: z.string().nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const followUpsListInputSchema = z.object({
  state: z.array(followUpStateSchema).max(7).optional(),
  contactId: uuidSchema.optional(),
  signal: digestSignalSchema.optional(),
  /** Default true: a snoozed item is hidden until its time comes. */
  hideSnoozed: z.boolean().default(true),
  limit: z.int().min(1).max(200).default(50),
});

export const followUpsListOutputSchema = z.object({
  items: z.array(followUpSchema),
});

export const followUpIdInputSchema = z.object({ followUpId: uuidSchema });

export const followUpSnoozeInputSchema = z.object({
  followUpId: uuidSchema,
  /** Must be in the future: a past snooze is a no-op that LOOKS like an action. */
  until: isoDateTimeSchema,
  reason: z.string().max(500).optional(),
});

export const followUpDismissInputSchema = z.object({
  followUpId: uuidSchema,
  /**
   * Optional, and stored as null when absent rather than as "". The reason is
   * the whole value of a dismissal to the learning loop, so an empty string
   * masquerading as one would poison it.
   */
  reason: z.string().max(500).optional(),
  /** "Never suggest this person again" — a stronger signal than one dismissal. */
  neverSuggest: z.boolean().default(false),
});

// --- draft mutations ---------------------------------------------------------

export const draftIdInputSchema = z.object({ draftId: uuidSchema });

export const draftUpdateInputSchema = z.object({
  draftId: uuidSchema,
  body: nonBlankString(20_000, 'body').optional(),
  subject: z.string().max(500).nullish(),
  channel: channelSchema.optional(),
  targetAddress: z.string().max(320).nullish(),
});

export const draftRegenerateInputSchema = z.object({
  draftId: uuidSchema,
  /** Free-text steer: "shorter", "mention the deck". Fed to the drafting task. */
  instruction: z.string().max(500).optional(),
});

export const draftMarkSentInputSchema = z.object({
  draftId: uuidSchema,
  /** For the LinkedIn / SMS paths, where the user sent it themselves. */
  channel: channelSchema.optional(),
});

/**
 * §2.5's send gate, on the wire.
 *
 * `confirm` is the explicit acknowledgement the design requires for a
 * `CATCH_ALL` / `HIGH_PROBABILITY` address and for a send outside recipient-local
 * quiet hours. It is one flag rather than two because the UI shows ONE dialog
 * listing whatever applies — and because a client that had to enumerate the
 * reasons would drift from the server's list.
 *
 * There is no `sendAt`. Nothing schedules; §2.5 is categorical that "nothing
 * ever sends without a human tap".
 */
export const draftSendEmailInputSchema = z.object({
  draftId: uuidSchema,
  confirm: z.boolean().default(false),
  /** Which mailbox to send from. Defaults to the account that owns the thread. */
  connectedAccountId: uuidSchema.optional(),
});

export const SEND_BLOCK_CODES = [
  'address_invalid',
  'address_unverified',
  'contact_cap',
  'daily_cap',
  'quiet_hours',
] as const;
export const sendBlockCodeSchema = z.enum(SEND_BLOCK_CODES);

export const draftSendEmailOutputSchema = z.object({
  ok: z.boolean(),
  draftId: uuidSchema,
  status: draftStatusSchema,
  sentAt: isoDateTimeSchema.nullable(),
  /** The provider receipt, so the sync echo can be attributed back. */
  rfcMessageId: z.string().nullable(),
  providerMessageId: z.string().nullable(),
  /** Present when the send was REFUSED. `overridable` says whether confirm helps. */
  blocked: z
    .object({
      code: sendBlockCodeSchema,
      overridable: z.boolean(),
      message: z.string(),
    })
    .nullable(),
  /** Rules a confirmation overrode. Shown after the fact, never hidden. */
  warnings: z.array(sendBlockCodeSchema),
});

export type DigestDto = z.output<typeof digestSchema>;
export type DigestItemDto = z.output<typeof digestItemSchema>;
export type DraftDto = z.output<typeof draftSchema>;
export type FollowUpDto = z.output<typeof followUpSchema>;
export type HermesProfileDto = z.output<typeof hermesProfileSchema>;
export type HermesMemoryDto = z.output<typeof hermesMemorySchema>;
